import { AveragePointObj, ChartData, ChartTicks, DailyScoreRecord, Stats } from "@model";
import { getTodaysDay, msInDay } from "./index";

export function fillMissingRecordsData(
  loadedRecords: DailyScoreRecord[],
  startDay: number,
  endDay: number,
  defaultDailyGoal: number,
): DailyScoreRecord[] {
  let lastSetGoal = defaultDailyGoal;
  const records: DailyScoreRecord[] = [];

  for (let i = startDay; i <= endDay; i++) {
    const savedRecord = loadedRecords.find((r) => r.dailyScoreDay === i);
    if (savedRecord?.dailyScoreGoal) {
      lastSetGoal = savedRecord.dailyScoreGoal;
    }
    records.push({
      dailyScoreDay: i,
      dailyScorePoints: savedRecord?.dailyScorePoints ?? 0,
      dailyScoreGoal: lastSetGoal,
    });
  }
  return records;
}

export function generateStats(records: DailyScoreRecord[], dayJoined: number): Stats {
  const userDayCount = getTodaysDay() - dayJoined;

  const averageDailyScore100 = getAverageDailyScore(records, userDayCount, 100);
  const averageDailyScore30 = getAverageDailyScore(records, userDayCount, 30);
  const averageDailyScore7 = getAverageDailyScore(records, userDayCount, 7);
  const allDailyScoreDaysArray = getAllDailyScoreDaysChartArray(records);

  const chartDataArrays: ChartData = {
    allDailyGoalsChartArray: getAllDailyGoalsChartArray(records),
    allDailyScorePointsChartArray: getAllDailyScorePointsChartArray(records),
    allDailyScoreDaysArray: allDailyScoreDaysArray,
    averageValueChartArray100: getAverageValueChartArray(records, userDayCount, 100),
    averageValueChartArray30: getAverageValueChartArray(records, userDayCount, 30),
    averageValueChartArray7: getAverageValueChartArray(records, userDayCount, 7),
    maxXScaleValue: getXScaleMaxValue(records),
    dateLabels: getDateLabels(records),
    emptyLabels: getEmptyLabels(records),
    chartTicks: getChartTicks(allDailyScoreDaysArray),
  };

  return {
    averageDailyScore100,
    averageDailyScore30,
    averageDailyScore7,
    chartDataArrays,
    chartDataLength: records.length,
  };
}

function getAverageDailyScore(
  records: DailyScoreRecord[],
  userDayCount: number,
  count: number,
): AveragePointObj {
  if (userDayCount < count) {
    return {
      active: false,
      value: null,
      willBeActiveInDaysCount: count - userDayCount,
    };
  } else {
    const sum = records.slice(-count).reduce((a, b) => a + b.dailyScorePoints, 0);
    const value = Math.round(sum / count);
    return {
      active: true,
      value: value,
      willBeActiveInDaysCount: null,
    };
  }
}

function getAllDailyGoalsChartArray(records: DailyScoreRecord[]): number[] {
  return records.map((record) => record.dailyScoreGoal);
}

function getAllDailyScorePointsChartArray(records: DailyScoreRecord[]): number[] {
  return records.map((record) => record.dailyScorePoints);
}

function getAllDailyScoreDaysChartArray(records: DailyScoreRecord[]): number[] {
  return records.map((record) => record.dailyScoreDay);
}

function getAverageValueChartArray(
  records: DailyScoreRecord[],
  userDayCount: number,
  count: number,
): number[] | null {
  if (userDayCount < count) {
    return null;
  } else {
    const sum = records
      .slice(-count)
      .map((record) => record.dailyScorePoints)
      .reduce((a, b) => a + b, 0);
    const average = Math.round(sum / count);
    return Array(count).fill(average);
  }
}

function getXScaleMaxValue(records: DailyScoreRecord[]): number {
  const PERCENTILE = 80;
  const MIN_X_SCALE_MAX_VALUE = 600;
  const values = records.map((r) => r.dailyScorePoints);
  const notEmptyValues = values.filter((r) => r);
  const sorted = notEmptyValues.sort((a, b) => a - b);
  const percentileIndex = Math.floor((sorted.length / 100) * PERCENTILE);
  const percentileValue = sorted[percentileIndex];
  const roundedToHundred = Math.ceil(percentileValue / 100) * 100;
  return roundedToHundred > MIN_X_SCALE_MAX_VALUE ? roundedToHundred : MIN_X_SCALE_MAX_VALUE;
}

function getDateLabels(records: DailyScoreRecord[]): string[] {
  return records.map((r) => {
    const day = r.dailyScoreDay;
    return new Date(day * msInDay).toLocaleDateString();
  });
}

function getEmptyLabels(records: DailyScoreRecord[]): string[] {
  return records.map(() => "");
}

function getChartTicks(days: number[]): ChartTicks {
  const ticksWeekDay: number[] = [];
  const ticksMonths: number[] = [];
  const ticksWeekBegin: number[] = [];
  const ticksMonthBegin: number[] = [];

  days.forEach((day, index) => {
    const date = new Date(day * msInDay);
    ticksWeekDay.push(date.getDay());
    ticksMonths.push(date.getMonth());
    if (date.getDay() === 1) {
      ticksWeekBegin.push(index);
    }
    if (date.getDate() === 1) {
      ticksMonthBegin.push(index);
    }
  });

  return {
    ticksWeekDay,
    ticksMonths,
    ticksWeekBegin,
    ticksMonthBegin,
  };
}
